import { useEffect, useState } from 'react';

export const useLocalStorage = <T extends string>(
    key: string,
    defaultValue: T,
    persist: boolean = true
): [T, React.Dispatch<React.SetStateAction<T>>] => {
    const [value, setValue] = useState(defaultValue);

    // If your key changes, load up the value from localStorage or set it to the provided default value
    useEffect(() => {
        if (!persist) return;
        try {
            const currentValue = localStorage.getItem(key) as T | null;
            if (currentValue === null) {
                setValue(defaultValue);
            } else if (value !== currentValue) {
                setValue(currentValue);
            }
        } catch (error) {
            console.error(`Error reading ${key} from localStorage`, error);
        }
    }, [key]);

    // Update localStorage when the value changes
    useEffect(() => {
        if (!persist) return;
        localStorage.setItem(key, value);
    }, [value]);

    return [value, setValue];
};

export const useLocalObjectStorage = <T extends object>(key: string, defaultValue: T, persist: boolean = true): [T, (value: T) => void] => {
    const [value, setValue] = useLocalStorage(key, JSON.stringify(defaultValue), persist);

    const setObjectValue = (value: T) => {
        setValue(JSON.stringify(value));
    };

    return [JSON.parse(value) as T, setObjectValue];
};

export default useLocalStorage;
